/**
 * TemplateInput — sl-e0il.1.2 + sl-zjgl post-Phase-5 refactor.
 *
 * Single-line CodeMirror editor for short template fields (HTTP URL,
 * header row key/value). Replaces the previous `<input type="text">`
 * implementation so the same decoration pipeline that powers
 * `<TemplateEditor>` (pills, autocomplete, syntax markers) reaches
 * single-line fields too.
 *
 * API: identical to the old `<input>`-backed component — same props,
 * same `EditorAdapter` ref contract. Callers that pass `extensions`
 * get the full decoration experience; callers that don't still get a
 * functional plain-text single-line editor (no visual regression).
 *
 * Single-line enforcement: a `transactionFilter` blocks any transaction
 * that would produce a newline (typed Enter, pasted multi-line blob,
 * adapter insert). See `internal/single-line.ts`.
 */

import type { Extension } from '@codemirror/state';
import { tooltips } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import * as styles from './editor.css';
import type { EditorAdapter } from './editor-adapter';
import {
  createCodeMirrorAdapter,
  useEchoSuppressedValue,
} from './internal/codemirror-helpers';
import { singleLineTransactionFilter } from './internal/single-line';

export interface TemplateInputProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /**
   * Extra extensions — typically `templateDecoration({ resolver })` +
   * autocomplete. When omitted the editor behaves like a plain
   * single-line text input with no pill rendering.
   */
  extensions?: readonly Extension[];
  /** See `CodeEditorProps.tooltipsParent`. */
  tooltipsParent?: HTMLElement | null;
}

export const TemplateInput = forwardRef<EditorAdapter, TemplateInputProps>(
  function TemplateInput(props, ref) {
    const viewRef = useRef<import('@codemirror/view').EditorView | null>(null);

    useImperativeHandle(
      ref,
      () =>
        createCodeMirrorAdapter({
          get current() {
            return viewRef.current;
          },
        }),
      []
    );

    // Strip any pre-existing newlines so a value persisted before the
    // single-line filter was wired doesn't pin the editor at >1 line.
    // The transaction filter prevents new ones from entering.
    const sanitizedValue = useMemo(
      () => props.value.replace(/\r?\n/g, ' '),
      [props.value]
    );
    const { value: guardedValue, onChange: guardedOnChange } =
      useEchoSuppressedValue(sanitizedValue, props.onChange);

    const tooltipsParent = props.tooltipsParent;
    const extensions = useMemo(
      () => [
        tooltips({ parent: tooltipsParent ?? document.body }),
        singleLineTransactionFilter(),
        ...(props.extensions ?? []),
      ],
      [tooltipsParent, props.extensions]
    );

    const rootClassName = [
      styles.editorRoot,
      styles.templateInputRoot,
      props.readOnly ? styles.editorReadOnly : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={rootClassName}>
        <CodeMirror
          value={guardedValue}
          onChange={guardedOnChange}
          extensions={extensions}
          readOnly={props.readOnly}
          placeholder={props.placeholder}
          height="auto"
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
            highlightActiveLine: false,
            highlightActiveLineGutter: false,
            bracketMatching: false,
            closeBrackets: false,
            autocompletion: false,
            indentOnInput: false,
          }}
          onCreateEditor={view => {
            viewRef.current = view;
          }}
        />
      </div>
    );
  }
);
